import React, { useMemo, useState } from 'react';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../../../../theme/tokens';
import { DateField, NumberField } from '../../../ui';
import { DetailCard, Dot, Field, FieldGrid, FieldLabel, LinkButton, Pill, RemoveX, SelectInput, StatStrip, TabBody, TextInput, ToolButton } from '../primitives';
import { dateInput, ProductDraft, PromotionDraft, toDate } from '../productDraft';
import type { AdminCanonicalProduct, PriceCheckIssue, ProductRelated, RelatedRebate } from '../productDetailTypes';
import { formatDate, formatDateTime, formatShortDate, formatUtcDate, money, pricingSource } from '../productDetailFormat';
import { StoreOverridesDialog } from '../StoreOverridesDialog';

interface Props {
  product: AdminCanonicalProduct;
  draft: ProductDraft;
  base: ProductDraft;
  editing: boolean;
  update: (patch: Partial<ProductDraft>) => void;
  priceCheck: PriceCheckIssue | null;
  related: ProductRelated;
  runningTools: Record<string, boolean>;
  runTool: (tool: 'price-check' | 'market-pricing') => void;
}

const DAY = 86_400_000;
const CURRENCY_OPTIONS = ['USD', 'CAD'];

/** The old editor's hint: what MAP less the instant rebate comes to, for the advertised floor. */
export function rebateAfterIrHint(mapValue: string, rebateValue: string): string {
  const map = Number(mapValue);
  const rebate = Number(rebateValue);
  if (!(map > 0) || !(rebate > 0)) return 'Standalone IR only. Empty clears. Copy MAP − IR into Sale for the advertised floor.';
  return `MAP − IR = ${Math.max(map - rebate, 0).toFixed(2)}. Copy that into Sale if it should be the advertised floor.`;
}

const rebateWindowHint = (draft: ProductDraft): string | undefined =>
  draft.instantRebate && (draft.rebateStartAt || draft.rebateEndAt)
    ? `${draft.rebateStartAt ? formatDate(draft.rebateStartAt) : '—'} → ${draft.rebateEndAt ? formatDate(draft.rebateEndAt) : 'open'}`
    : undefined;

type WindowBar = { label: string; sub: string; start: number | null; end: number | null; tone: 'neutral' | 'info' | 'accent' | 'promo' | 'promoStack' };

export const PricingTab: React.FC<Props> = ({ product, draft, base, editing, update, priceCheck, related, runningTools, runTool }) => {
  const currency = product.pricing?.currency || 'USD';
  const pricing = product.pricing || {};
  const verified = pricing.verification;
  const failing = !!priceCheck;
  const [overridesOpen, setOverridesOpen] = useState(false);
  const storeOverrides = related.storeOverrides || [];

  // ---- price fields
  const priceFields: Array<{ key: keyof ProductDraft & string; label: string; hint?: string }> = [
    { key: 'msrp', label: 'MSRP' },
    { key: 'map', label: 'MAP' },
    { key: 'retail', label: 'Retail' },
    { key: 'salePrice', label: 'Sale', hint: draft.salePriceSource ? draft.salePriceSource.replace('_', ' ') : undefined },
    { key: 'instantRebate', label: 'Instant rebate', hint: editing ? rebateAfterIrHint(draft.map, draft.instantRebate) : rebateWindowHint(draft) },
    { key: 'wholesale', label: 'Wholesale', hint: 'dealer only' },
    { key: 'tradeCredit', label: 'Trade credit', hint: 'dealer only' },
  ];

  // ---- timeline
  const now = Date.now();
  const bars: WindowBar[] = useMemo(() => {
    const t = (v: string) => toDate(v)?.getTime() ?? null;
    const list: WindowBar[] = [
      {
        label: `MSRP ${money(Number(draft.msrp) || null, currency)}`,
        sub: `manufacturerPriceAsOf ${draft.manufacturerPriceAsOf ? formatDate(draft.manufacturerPriceAsOf) : '—'}${draft.manufacturerPriceEndAt ? ` → ${formatDate(draft.manufacturerPriceEndAt)}` : ' · open-ended'}`,
        start: t(draft.manufacturerPriceAsOf),
        end: t(draft.manufacturerPriceEndAt),
        tone: 'neutral',
      },
    ];
    if (draft.map) {
      list.push({
        label: `MAP ${money(Number(draft.map), currency)}`,
        sub: `${draft.mapStartAt ? formatDate(draft.mapStartAt) : '—'} → ${draft.mapEndAt ? formatDate(draft.mapEndAt) : 'open'}`,
        start: t(draft.mapStartAt),
        end: t(draft.mapEndAt),
        tone: 'info',
      });
    }
    if (draft.salePrice) {
      list.push({
        label: `Sale ${money(Number(draft.salePrice), currency)}`,
        sub: `${draft.salePriceStartAt ? formatDate(draft.salePriceStartAt) : '—'} → ${draft.salePriceEndAt ? formatDate(draft.salePriceEndAt) : 'open'}${draft.salePriceSource ? ` · ${draft.salePriceSource}` : ''}`,
        start: t(draft.salePriceStartAt),
        end: t(draft.salePriceEndAt),
        tone: 'accent',
      });
    }
    if (draft.instantRebate) {
      list.push({
        label: `Instant rebate −${money(Number(draft.instantRebate), currency)}`,
        sub: `${draft.rebateStartAt ? formatDate(draft.rebateStartAt) : '—'} → ${draft.rebateEndAt ? formatDate(draft.rebateEndAt) : 'open'}`,
        start: t(draft.rebateStartAt),
        end: t(draft.rebateEndAt),
        tone: 'promo',
      });
    }
    if (draft.effectiveDate || draft.expirationDate) {
      list.push({
        label: 'Pricing in effect',
        sub: `effectiveDate ${draft.effectiveDate ? formatDate(draft.effectiveDate) : '—'} → ${draft.expirationDate ? formatDate(draft.expirationDate) : 'open'}`,
        start: t(draft.effectiveDate),
        end: t(draft.expirationDate),
        tone: 'neutral',
      });
    }
    for (const promo of draft.promotions) {
      list.push({
        label: `${promo.name || 'Promotion'} −${money(Number(promo.amount) || 0, currency)}`,
        sub: `${promo.startsAt ? formatDate(promo.startsAt) : '—'} → ${promo.endsAt ? formatDate(promo.endsAt) : 'open'}${promo.stackable === 'Yes' ? ' · stackable' : ''}`,
        start: t(promo.startsAt),
        end: t(promo.endsAt),
        tone: promo.stackable === 'Yes' ? 'promoStack' : 'promo',
      });
    }
    return list;
  }, [draft, currency]);

  const sheetBars: WindowBar[] = useMemo(() => {
    return (related.priceWindows || []).map((window) => {
      const parts: string[] = [];
      if (window.mapPrice != null) parts.push(`MAP ${money(window.mapPrice, currency)}`);
      if (window.instantRebate != null) parts.push(`rebate ${money(window.instantRebate, currency)}`);
      const start = window.startAt ? formatUtcDate(window.startAt) : '';
      const end = window.endAt ? formatUtcDate(window.endAt) : '';
      const advertised = window.advertisedPrice != null ? ` · advertised ${money(window.advertisedPrice, currency)}` : '';
      return {
        label: parts.join(' · ') || 'Price window',
        sub: `${start || '—'} → ${end || 'open'}${advertised}${window.pending ? ' · pending' : ''}`,
        start: toDate(window.startAt || '')?.getTime() ?? null,
        end: toDate(window.endAt || '')?.getTime() ?? null,
        tone: window.pending ? 'promo' : 'accent',
      };
    });
  }, [related.priceWindows, currency]);

  const range = useMemo(() => {
    const points = [...bars, ...sheetBars].flatMap((b) => [b.start, b.end]).filter((x): x is number => x != null);
    const lo = Math.min(now - 60 * DAY, ...points);
    const hi = Math.max(now + 90 * DAY, ...points);
    const start = new Date(lo);
    start.setDate(1);
    const end = new Date(hi);
    end.setMonth(end.getMonth() + 1, 0);
    return { r0: start.getTime(), r1: end.getTime() };
  }, [bars, sheetBars, now]);
  const pct = (time: number) => Math.max(0, Math.min(100, ((time - range.r0) / (range.r1 - range.r0)) * 100));
  const months = useMemo(() => {
    const out: Array<{ label: string; left: number }> = [];
    const cursor = new Date(range.r0);
    while (cursor.getTime() <= range.r1) {
      out.push({ label: cursor.toLocaleDateString('en-US', { month: 'short' }), left: pct(cursor.getTime()) });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);
  const monthStep = months.length > 8 ? Math.ceil(months.length / 8) : 1;
  const barFill = (tone: WindowBar['tone']) =>
    ({
      neutral: tokens.ink.disabled,
      info: tokens.accent.border,
      accent: tokens.accent.main,
      promo: tokens.signal.info.solid,
      promoStack: tokens.accent.tintStrong,
    })[tone];

  const renderBar = (bar: WindowBar, key: string) => {
    const left = bar.start == null ? 0 : pct(bar.start);
    const right = bar.end == null ? 100 : pct(bar.end);
    return (
      <React.Fragment key={key}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1px', minHeight: 40, borderTop: `1px solid ${tokens.line.faint}` }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>{bar.label}</Typography>
          <Typography sx={{ fontSize: 11, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{bar.sub}</Typography>
        </Box>
        <Box sx={{ position: 'relative', minHeight: 40, borderTop: `1px solid ${tokens.line.faint}` }}>
          <Box sx={{ position: 'absolute', top: 11, height: 18, left: `${left}%`, width: `${Math.max(0.8, right - left)}%`, bgcolor: barFill(bar.tone), borderRadius: '5px' }} />
          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(now)}%`, width: 2, bgcolor: tokens.signal.problem.solid, opacity: 0.55 }} aria-hidden />
        </Box>
      </React.Fragment>
    );
  };

  const pendingSheetWindows = (related.priceWindows || []).some((window) => window.pending);

  // ---- market
  const market = pricing.marketPricing;
  const dealers = market?.dealerPrices || [];
  const dealerMisses = market?.dealerMisses || [];
  const prices = dealers.map((d) => d.price).filter((p) => Number.isFinite(p));
  const low = market?.streetPriceLow ?? (prices.length ? Math.min(...prices) : undefined);
  const high = market?.streetPriceHigh ?? (prices.length ? Math.max(...prices) : undefined);
  const avg = market?.streetPriceAvg ?? (prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : undefined);
  const retail = pricing.retail;
  const diff = retail != null && avg != null ? retail - avg : null;
  const marketLine =
    avg == null
      ? 'No market prices yet. Refresh to pull street prices from authorized dealers.'
      : `${retail != null ? `Your retail ${money(retail, currency)} is ${money(Math.abs(diff || 0), currency)} ${diff! >= 0 ? 'above' : 'below'} the street average.` : 'No retail price set.'} ${dealers.length} dealer${dealers.length === 1 ? '' : 's'} reporting.`;
  const lowestDealer = dealers.length ? dealers.reduce((a, b) => (b.price < a.price ? b : a)) : null;

  const setPromo = (i: number, patch: Partial<PromotionDraft>) =>
    update({ promotions: draft.promotions.map((p, j) => (j === i ? { ...p, ...patch } : p)) });

  return (
    <TabBody>
      {/* 1. Price-check health */}
      <DetailCard
        title="Price-check health"
        tone={failing ? 'problem' : undefined}
        headerAlign="center"
        actions={
          <ToolButton running={runningTools['price-check']} onClick={() => runTool('price-check')} disabled={!product.source?.sourceUrl} tooltip={product.source?.sourceUrl ? 'Queue a price-only check of the source page' : 'No source page to check'}>
            {runningTools['price-check'] ? 'Checking…' : 'Retry check now'}
          </ToolButton>
        }
      >
        <Box sx={{ px: '18px', mt: -0.5 }}>
          {failing ? <Pill tone="problem">Failing</Pill> : verified?.status === 'verified' ? <Pill tone="positive">Verified</Pill> : <Pill tone="neutral">Not checked</Pill>}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fill, minmax(170px, 1fr))' }, gap: '4px 20px', px: '18px', pt: 0.5, pb: 1.5 }}>
          {[
            { k: 'verification.status', v: verified?.status || '—', tone: verified?.status === 'unverified' ? 'problem' : undefined },
            { k: 'lastVerifiedAt', v: formatDateTime(verified?.lastVerifiedAt) || '—' },
            { k: 'priceLastChecked', v: formatDateTime(pricing.priceLastChecked) || '—' },
            { k: 'priceCheckFailCount', v: String(pricing.priceCheckFailCount ?? 0), tone: (pricing.priceCheckFailCount || 0) > 0 ? 'problem' : undefined },
            { k: 'priceAsOf', v: formatDateTime(pricing.priceAsOf) || '—' },
            { k: 'vendorPriceAsOf', v: formatDateTime(pricing.vendorPriceAsOf) || '—' },
          ].map((h) => (
            <Box key={h.k} sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, py: 0.75 }}>
              <FieldLabel mono>{h.k}</FieldLabel>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: h.tone ? tokens.signal.problem.ink : tokens.ink.primary, fontVariantNumeric: 'tabular-nums' }}>{h.v}</Typography>
            </Box>
          ))}
        </Box>
        {pricing.priceCheckLastError && failing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mx: '18px', mb: 2, px: 1.5, py: 1.25, borderRadius: '9px', bgcolor: tokens.signal.problem.tint, border: `1px solid ${tokens.signal.problem.border}` }}>
            <FieldLabel mono>priceCheckLastError</FieldLabel>
            <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 12.5, color: tokens.signal.problem.ink, overflowWrap: 'anywhere' }}>
              {pricing.priceCheckLastError}
              {pricing.priceCheckFailedAt ? ` — ${formatDateTime(pricing.priceCheckFailedAt)}` : ''}
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.signal.problem.ink }}>
              Retail and sale prices haven’t been confirmed against the source
              {verified?.lastVerifiedAt ? ` since ${formatDate(verified.lastVerifiedAt)}` : ''}. Stores still see the last verified values.
            </Typography>
          </Box>
        )}
      </DetailCard>

      {/* 2. Current prices */}
      <DetailCard title="Current prices" note={`${currency} · standalone product, not kit or combo`}>
        <FieldGrid min={140}>
          {priceFields.map((f) => {
            const { source, label } = pricingSource(product, f.key, String(draft[f.key]), String(base[f.key]));
            return (
              <Field
                key={f.key}
                label={f.label}
                source={source}
                sourceLabel={label}
                hint={f.hint}
                valueSize={15}
                display={draft[f.key] ? money(Number(draft[f.key]), currency) : ''}
                editor={
                  editing ? (
                    <NumberField value={String(draft[f.key])} onChange={(v) => update({ [f.key]: v } as Partial<ProductDraft>)} min={0} step={0.01} adornment="$" aria-label={f.label} sx={{ width: '100%' }} />
                  ) : undefined
                }
              />
            );
          })}
          <Field
            label="Currency"
            source={draft.currency !== base.currency ? { source: 'manual', detail: 'Edited, not saved yet' } : null}
            valueSize={15}
            display={draft.currency}
            editor={editing ? <SelectInput ariaLabel="Currency" value={draft.currency} options={CURRENCY_OPTIONS} onChange={(currency) => update({ currency })} /> : undefined}
          />
        </FieldGrid>
        <Typography sx={{ px: '18px', pb: 1.75, fontSize: 11.5, lineHeight: 1.5, color: tokens.ink.muted }}>
          Rebate override is for the standalone SKU only — do not enter sold-with or combo amounts. Clear the rebate and sale fields to remove a stamp. A later manufacturer price sheet can still replace what you save here.
        </Typography>
      </DetailCard>

      {/* 3. Price windows */}
      <DetailCard title="Price windows" note={`${new Date(range.r0).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – ${new Date(range.r1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} · today ${formatShortDate(new Date(now))}`}>
        <Box sx={{ px: '18px', pb: 1.75, overflowX: 'auto' }}>
          <Box sx={{ minWidth: 640, display: 'grid', gridTemplateColumns: { xs: '220px minmax(0, 1fr)', md: '240px minmax(0, 1fr)' }, columnGap: 1.75 }}>
            <span />
            <Box sx={{ position: 'relative', height: 20 }}>
              {months.map((m, i) =>
                i % monthStep === 0 ? (
                  <Typography key={`${m.label}-${i}`} component="span" sx={{ position: 'absolute', left: `${m.left}%`, top: 0, fontSize: 11, color: tokens.ink.faint, pl: 0.5, borderLeft: `1px solid ${tokens.line.default}`, height: 16 }}>
                    {m.label}
                  </Typography>
                ) : null,
              )}
            </Box>
            {bars.map((bar, i) => renderBar(bar, `product-${bar.label}-${i}`))}
            {sheetBars.length > 0 && (
              <Box sx={{ gridColumn: '1 / -1', pt: 1.5, pb: 0.25 }}>
                <Typography variant="overline">Sheet windows</Typography>
              </Box>
            )}
            {sheetBars.map((bar, i) => renderBar(bar, `sheet-${bar.sub}-${i}`))}
          </Box>
        </Box>
        {pendingSheetWindows && (
          <Typography sx={{ px: '18px', pb: 1.75, mt: -0.5, fontSize: 12, lineHeight: 1.5, color: tokens.ink.muted }}>
            Pending windows come from a sheet that is not approved yet. They are not the live price.
          </Typography>
        )}
        {editing && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fill, minmax(160px, 1fr))' }, gap: '10px 16px', px: '18px', py: 1.5, borderTop: `1px solid ${tokens.line.hairline}`, bgcolor: tokens.signal.caution.row }}>
            {(['manufacturerPriceAsOf', 'manufacturerPriceEndAt', 'mapStartAt', 'mapEndAt', 'salePriceStartAt', 'salePriceEndAt', 'rebateStartAt', 'rebateEndAt', 'effectiveDate', 'expirationDate'] as const).map((key) => (
              <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FieldLabel mono>{key}</FieldLabel>
                <DateField value={draft[key]} onChange={(v) => update({ [key]: v } as Partial<ProductDraft>)} aria-label={key} sx={{ width: '100%' }} />
              </Box>
            ))}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FieldLabel mono>salePriceSource</FieldLabel>
              <SelectInput ariaLabel="salePriceSource" value={draft.salePriceSource} options={['scrape', 'map_rebate', 'manual']} onChange={(salePriceSource) => update({ salePriceSource })} />
            </Box>
          </Box>
        )}
      </DetailCard>

      {/* 4. Promotions */}
      <DetailCard
        title="Active promotions"
        count={draft.promotions.length}
        actions={
          editing ? (
            <LinkButton
              onClick={() =>
                update({
                  promotions: [
                    ...draft.promotions,
                    { id: `manual_${Date.now().toString(36)}`, name: '', amount: '', startsAt: dateInput(new Date()), endsAt: '', stackable: 'No', type: 'instant_rebate', source: 'manual' },
                  ],
                })
              }
            >
              + Add promotion
            </LinkButton>
          ) : undefined
        }
      >
        {draft.promotions.length === 0 ? (
          <Typography sx={{ px: '18px', pb: 2, fontSize: 13, color: tokens.ink.faint }}>No active promotions.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 640 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(160px, 1fr) 110px 140px 140px 90px 28px', md: 'minmax(160px, 1fr) 110px 140px 140px 90px 28px' }, gap: 1.25, px: '18px', pb: 0.75, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
                <span>Name</span>
                <Box component="span" sx={{ textAlign: 'right' }}>Amount</Box>
                <span>Starts</span>
                <span>Ends</span>
                <span>Stackable</span>
                <span />
              </Box>
              {draft.promotions.map((promo, i) => (
                <Box key={promo.id} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(160px, 1fr) 110px 140px 140px 90px 28px', md: 'minmax(160px, 1fr) 110px 140px 140px 90px 28px' }, gap: 1.25, alignItems: 'center', minHeight: 42, px: '18px', py: 0.5, borderTop: `1px solid ${tokens.line.faint}`, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                  {editing ? (
                    <>
                      <TextInput ariaLabel={`Promotion ${i + 1} name`} value={promo.name} onChange={(name) => setPromo(i, { name })} />
                      <NumberField value={promo.amount} onChange={(amount) => setPromo(i, { amount })} min={0} step={0.01} adornment="$" aria-label={`Promotion ${i + 1} amount`} />
                      <DateField value={promo.startsAt} onChange={(startsAt) => setPromo(i, { startsAt })} aria-label={`Promotion ${i + 1} start`} />
                      <DateField value={promo.endsAt} onChange={(endsAt) => setPromo(i, { endsAt })} aria-label={`Promotion ${i + 1} end`} />
                      <SelectInput ariaLabel={`Promotion ${i + 1} stackable`} value={promo.stackable} options={['Yes', 'No']} onChange={(v) => setPromo(i, { stackable: v as 'Yes' | 'No' })} />
                      <RemoveX label={`Remove promotion ${i + 1}`} onClick={() => update({ promotions: draft.promotions.filter((_, j) => j !== i) })} />
                    </>
                  ) : (
                    <>
                      <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.primary }}>{promo.name}</Typography>
                      <Typography component="span" sx={{ fontSize: 13, textAlign: 'right' }}>{money(Number(promo.amount) || 0, currency)}</Typography>
                      <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.bodyAlt }}>{formatDate(promo.startsAt) || '—'}</Typography>
                      <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.bodyAlt }}>{formatDate(promo.endsAt) || '—'}</Typography>
                      <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.bodyAlt }}>{promo.stackable}</Typography>
                      <span />
                    </>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DetailCard>

      {/* 5. Rebates & bundles */}
      <DetailCard
        title="Rebates & bundles"
        count={related.rebates.length}
        note="Programs this SKU qualifies for, and the SKUs that go with them"
        footer="Programs and bundle rules are edited in their own records. Changes there show here on the next sync."
      >
        {related.rebates.length === 0 && <Typography sx={{ px: '18px', pb: 2, fontSize: 13, color: tokens.ink.faint }}>This product is in no rebate program or bundle rule.</Typography>}
        {related.rebates.map((r) => (
          <RebateRow key={`${r.kind}-${r.id}`} rebate={r} currency={currency} now={now} />
        ))}
      </DetailCard>

      {/* 6. Market & dealer prices */}
      <DetailCard
        title="Market & dealer prices"
        note={`marketPricing · ${market?.checkedAt ? `checked ${formatDateTime(market.checkedAt)}` : 'never checked'}${market?.confidence != null ? ` · ${Math.round(market.confidence * 100)}% confident` : ''}`}
        headerAlign="center"
        actions={
          <ToolButton running={runningTools['market-pricing']} onClick={() => runTool('market-pricing')}>
            Refresh market prices
          </ToolButton>
        }
      >
        <Typography sx={{ px: '18px', pb: 1.25, fontSize: 12.5, color: tokens.ink.muted }}>{marketLine}</Typography>
        <Box sx={{ px: '18px', pb: 1.5 }}>
          <StatStrip
            items={[
              { label: 'Low', value: money(low, currency) },
              { label: 'Average', value: money(avg, currency) },
              { label: 'High', value: money(high, currency) },
              { label: 'Your retail', value: money(retail, currency), highlight: true },
            ]}
          />
        </Box>
        {(dealers.length > 0 || dealerMisses.length > 0) && (
          <Box sx={{ overflowX: 'auto', pb: 0.75 }}>
            <Box sx={{ minWidth: 700 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(130px, 1fr) 100px 130px 130px 100px 150px', md: 'minmax(130px, 1fr) 100px 130px 130px 100px 150px' }, gap: 1.25, px: '18px', pb: 0.75, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
                <span>Dealer</span>
                <Box component="span" sx={{ textAlign: 'right' }}>Price</Box>
                <span>Stock</span>
                <span>Source</span>
                <span>Confidence</span>
                <span>Checked</span>
              </Box>
              {dealers.map((d, i) => (
                <Box key={`${d.dealer}-${i}`} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(130px, 1fr) 100px 130px 130px 100px 150px', md: 'minmax(130px, 1fr) 100px 130px 130px 100px 150px' }, gap: 1.25, alignItems: 'center', minHeight: 42, px: '18px', borderTop: `1px solid ${tokens.line.faint}`, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    {d.url ? (
                      <MuiLink href={d.url} target="_blank" rel="noreferrer" underline="hover" noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{d.dealer}</MuiLink>
                    ) : (
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{d.dealer}</Typography>
                    )}
                    {lowestDealer === d && <Pill tone="positive" dot={false}>lowest</Pill>}
                  </Stack>
                  <Typography component="span" sx={{ textAlign: 'right', fontWeight: 600, fontSize: 12.5 }}>{money(d.price, currency)}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Dot tone={d.inStock === true ? 'positive' : d.inStock === false ? 'caution' : 'neutral'} />
                    <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.body }}>{d.inStock === true ? 'In stock' : d.inStock === false ? 'Out of stock' : 'Unknown'}</Typography>
                  </Stack>
                  <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.muted }}>{d.source || '—'}</Typography>
                  <ConfidenceBar value={d.confidence} />
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{formatDateTime(d.observedAt) || '—'}</Typography>
                </Box>
              ))}
              {dealerMisses.map((m, i) => (
                <Box key={`miss-${m.dealer}-${i}`} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(130px, 1fr) 100px 130px 130px 100px 150px', md: 'minmax(130px, 1fr) 100px 130px 130px 100px 150px' }, gap: 1.25, alignItems: 'center', minHeight: 42, px: '18px', borderTop: `1px solid ${tokens.line.faint}`, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    {m.url ? (
                      <MuiLink href={m.url} target="_blank" rel="noreferrer" underline="hover" noWrap sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.muted }}>{m.dealer}</MuiLink>
                    ) : (
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.muted }}>{m.dealer}</Typography>
                    )}
                    <Pill tone="caution" dot={false}>not found</Pill>
                  </Stack>
                  <Typography component="span" title="The last price this dealer reported" sx={{ textAlign: 'right', fontSize: 12.5, color: tokens.ink.muted }}>{money(m.lastPrice, currency)}</Typography>
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{m.lastSeenAt ? `Last ok ${formatDate(m.lastSeenAt)}` : 'Last ok —'}</Typography>
                  <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.faint }}>missed</Typography>
                  <Typography component="span" title="Not counted in the street prices" sx={{ fontSize: 12.5, color: tokens.ink.faint }}>—</Typography>
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{formatDateTime(m.missedAt) || '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Typography sx={{ px: '18px', py: 1, fontSize: 11.5, color: tokens.ink.faint }}>Dealer rows are read-only. Each refresh replaces them; a dealer the latest refresh did not find stays listed with its last price for 90 days and is not counted in the street prices.</Typography>
      </DetailCard>

      {/* 7. Related records */}
      <DetailCard title="Related pricing records" note="Stored in other collections">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fill, minmax(220px, 1fr))' }, columnGap: 3, px: '18px', pb: 1.5 }}>
          {[
            { label: 'Price history', n: related.priceHistoryCount, href: '/app/admin/catalog-price-changes' },
            { label: 'Organization pricing rows', n: related.organizationPricingCount, href: '/app/admin/map-pricing' },
            { label: 'Store overrides', n: related.storeOverrideCount, href: undefined, onView: storeOverrides.length > 0 ? () => setOverridesOpen(true) : undefined },
          ].map((r: { label: string; n: number; href?: string; onView?: () => void }) => (
            <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 38, borderBottom: `1px solid ${tokens.line.faint}` }}>
              <Typography component="span" sx={{ flex: '1 1 auto', fontSize: 13 }}>{r.label}</Typography>
              <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{r.n}</Typography>
              {r.href ? (
                <MuiLink component={RouterLink} to={r.href} underline="hover" sx={{ fontSize: 12.5, fontWeight: 600 }}>Open</MuiLink>
              ) : r.onView ? (
                <LinkButton onClick={r.onView}>View</LinkButton>
              ) : (
                <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.faint }}>{r.n > 0 ? 'Store-owned' : 'None'}</Typography>
              )}
            </Box>
          ))}
        </Box>
      </DetailCard>
      <StoreOverridesDialog
        open={overridesOpen}
        onClose={() => setOverridesOpen(false)}
        productName={product.name || product.sku || product.id}
        rows={storeOverrides}
        total={Math.max(related.storeOverrideCount, storeOverrides.length)}
        currency={currency}
      />
    </TabBody>
  );
};

/** Per-dealer confidence: a short bar plus the percent; ≥90% positive, ≥70% caution, else neutral. */
const ConfidenceBar: React.FC<{ value?: number }> = ({ value }) => {
  if (value == null || !Number.isFinite(value)) {
    return <Typography component="span" title="This row has no confidence rating" sx={{ fontSize: 12.5, color: tokens.ink.faint }}>—</Typography>;
  }
  const tone = value >= 0.9 ? 'positive' : value >= 0.7 ? 'caution' : 'neutral';
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ flex: '0 0 36px', height: 4, borderRadius: '2px', bgcolor: tokens.surface.tint, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.round(value * 100)}%`, bgcolor: tokens.signal[tone].solid }} />
      </Box>
      <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{Math.round(value * 100)}%</Typography>
    </Stack>
  );
};

const RebateRow: React.FC<{ rebate: RelatedRebate; currency: string; now: number }> = ({ rebate, currency, now }) => {
  const start = toDate(rebate.startAt)?.getTime();
  const end = toDate(rebate.endAt)?.getTime();
  const live = rebate.status === 'expired' || (end != null && end < now) ? 'ended' : start != null && start > now ? 'upcoming' : 'live';
  const liveLabel = live === 'live' ? 'Live now' : live === 'upcoming' ? `Starts ${formatShortDate(rebate.startAt)}` : 'Ended';
  const self = rebate.products.find((p) => p.self);
  const others = rebate.products.filter((p) => !p.self);
  const bundleTotal =
    rebate.kind === 'bundle' && rebate.amount != null && rebate.products.every((p) => p.price != null) && rebate.products.length > 1
      ? rebate.products.reduce((sum, p) => sum + (p.price || 0), 0)
      : null;
  const meta = [
    [formatDate(rebate.startAt), formatDate(rebate.endAt)].filter(Boolean).join(' → '),
    rebate.stacking ? rebate.stacking.replace(/_/g, ' ') : '',
    rebate.scope || '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: '18px', py: 1.75, borderTop: `1px solid ${tokens.line.hairline}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Pill tone={rebate.kind === 'bundle' ? 'neutral' : 'info'} dot={false}>
              {rebate.kind === 'bundle' ? 'BUNDLE' : 'INSTANT REBATE'}
            </Pill>
            <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 600 }}>{rebate.name}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <Dot tone={live === 'live' ? 'positive' : 'neutral'} />
              <Typography component="span" sx={{ fontSize: 11.5, color: live === 'live' ? tokens.signal.positive.ink : tokens.ink.muted }}>{liveLabel}</Typography>
            </Stack>
          </Stack>
          {meta && <Typography sx={{ fontSize: 12, color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>{meta}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{rebate.amount != null ? `−${money(rebate.amount, currency)}` : '—'}</Typography>
          {rebate.href && (
            <MuiLink component={RouterLink} to={rebate.href} underline="hover" sx={{ fontSize: 12, fontWeight: 600 }}>
              {rebate.kind === 'bundle' ? 'Open bundle rule' : 'Open rebate program'}
            </MuiLink>
          )}
        </Box>
      </Box>
      {rebate.products.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <FieldLabel>{rebate.kind === 'bundle' ? 'SKUs in this bundle' : 'Eligible SKUs'}</FieldLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fill, minmax(230px, 1fr))' }, gap: 0.75 }}>
            {[...(self ? [self] : []), ...others].map((p) => (
              <Box
                key={p.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  minHeight: 40,
                  px: 1.25,
                  py: 0.75,
                  border: `1px solid ${p.self ? tokens.accent.border : tokens.line.default}`,
                  borderRadius: '9px',
                  bgcolor: p.self ? tokens.signal.info.row : tokens.surface.card,
                }}
              >
                <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600 }}>{p.name || p.id}</Typography>
                  <Typography sx={{ fontSize: 11, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{p.sku || '—'}</Typography>
                </Box>
                {p.self ? <Pill tone="info" dot={false}>this SKU</Pill> : p.price != null ? <Typography sx={{ fontSize: 12, color: tokens.ink.bodyAlt }}>{money(p.price, currency)}</Typography> : null}
              </Box>
            ))}
          </Box>
          {bundleTotal != null && (
            <Typography sx={{ fontSize: 12, color: tokens.ink.bodyAlt, fontVariantNumeric: 'tabular-nums' }}>
              {money(bundleTotal, currency)} → {money(bundleTotal - (rebate.amount || 0), currency)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
